<template>
  <div class="border-b p-3">
    <Alert v-if="statusError" variant="destructive" class="mb-3">
      <AlertDescription>{{ statusError }}</AlertDescription>
    </Alert>

    <div class="flex items-center justify-between gap-3">
      <div class="min-w-0">
        <p class="text-xs font-medium text-muted-foreground">
          Status
        </p>
        <p class="truncate text-sm font-medium">
          {{ currentStatusLabel }}
        </p>
      </div>
      <Badge v-if="isUpdatingStatus" variant="secondary" class="shrink-0">
        Saving
      </Badge>
    </div>

    <Select :model-value="selectedStatus" :disabled="isBusy || options.length === 0" @update:model-value="updateStatus">
      <SelectTrigger class="mt-3 h-8">
        <SelectValue :placeholder="selectPlaceholder" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem v-for="option in options" :key="option.value" :value="option.value">
          {{ option.label }}
        </SelectItem>
      </SelectContent>
    </Select>
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'
import { Alert, AlertDescription } from '../../../../../../../resources/js/components/ui/alert/index.js'
import { Badge } from '../../../../../../../resources/js/components/ui/badge/index.js'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../../../resources/js/components/ui/select/index.js'
import { useUserStatusStore } from '../../stores/userStatusStore.js'

const userStatusStore = useUserStatusStore()
const { currentStatus, options, isLoadingStatus, isLoadingOptions, isUpdatingStatus } = storeToRefs(userStatusStore)

const statusError = ref('')

const selectedStatus = computed(() => currentStatus.value?.status || '')
const isBusy = computed(() => isLoadingStatus.value || isLoadingOptions.value || isUpdatingStatus.value)

const currentStatusLabel = computed(() => {
  if (currentStatus.value?.label) {
    return currentStatus.value.label
  }

  const matchingOption = options.value.find((option) => option.value === selectedStatus.value)

  return matchingOption?.label || 'Set status'
})

const selectPlaceholder = computed(() => {
  return isBusy.value ? 'Loading status' : 'Set status'
})

const loadStatus = async () => {
  statusError.value = ''

  try {
    await Promise.all([
      userStatusStore.fetchOptions(),
      userStatusStore.fetchCurrentStatus(),
    ])
  } catch (error) {
    statusError.value = errorMessageFrom(error, 'Unable to load your status.')
  }
}

const updateStatus = async (status) => {
  if (!status || status === selectedStatus.value) {
    return
  }

  statusError.value = ''

  try {
    await userStatusStore.updateStatus(status)
  } catch (error) {
    statusError.value = errorMessageFrom(error, 'Unable to update your status.')
  }
}

const errorMessageFrom = (error, fallback) => {
  const validationErrors = error?.response?.data?.errors

  if (validationErrors && typeof validationErrors === 'object') {
    const messages = Object.values(validationErrors).flat().filter(Boolean)

    if (messages.length > 0) {
      return messages[0]
    }
  }

  return error?.response?.data?.message || error?.message || fallback
}

onMounted(() => {
  loadStatus()
})
</script>
